/** Visitor location context — detects the visitor's place once (free IP
 *  geolocation via our backend), persists their confirmed choice, and exposes
 *  Set / Change behaviour to the whole site.
 *
 *  place:  { city, region, country, country_code, lat, lon, detected? }
 *  pinned: true once the visitor has confirmed ("Set") or picked a place.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import api from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ft_place";

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const [place, setPlace] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [detecting, setDetecting] = useState(true);

  const detect = useCallback(async () => {
    setDetecting(true);
    try {
      const { data } = await axios.get(`${API}/geo/locate`);
      if (data?.place?.city) setPlace(data.place);
    } catch {
      /* location simply stays unknown — pages degrade gracefully */
    } finally {
      setDetecting(false);
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setPlace(JSON.parse(saved));
        setPinned(true);
        setDetecting(false);
        return;
      }
    } catch { /* fall through to detection */ }
    detect();
  }, [detect]);

  /** Persist the visitor's place ("Set" / choosing from the Change picker).
   *  Signed-in members also save it to their account for the monthly digest. */
  const pinPlace = useCallback((p) => {
    setPlace(p);
    setPinned(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch { /* private mode — keep in memory only */ }
    api.post("/v1/geo/pin", p).catch(() => { /* anonymous or offline — fine */ });
  }, []);

  /** Forget the saved place and detect again. */
  const resetPlace = useCallback(() => {
    setPinned(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    detect();
  }, [detect]);

  return (
    <LocationContext.Provider value={{ place, pinned, detecting, pinPlace, resetPlace }}>
      {children}
    </LocationContext.Provider>
  );
}

export function usePlace() {
  return useContext(LocationContext);
}
